"use client";

import type { GamePageTranslations } from "@casino/server/graphql/getCmsGamePagePhrases";
import translatePhrase from "@casino/ui/Games/translatePhrase";
import type { TournamentFragment } from "@workspace/casino_core/fragments/TournamentFragment";
import { Button, PromoBanner, useMediaQuery } from "@workspace/frontend-ui";
import type { FC } from "react";
import getGamePageBanners from "./getGamePageBanners";
import getSecondsUntil from "./getSecondsUntil";
import useTournamentOptIn from "./hooks/useTournamentOptIn";
import MyTournamentStanding from "./MyTournamentStanding";
import MOBILE_MEDIA_QUERY from "./mobileMediaQuery";
import TournamentReadMoreButton from "./TournamentReadMoreButton";
import tournamentButtonLabel from "./tournamentButtonLabel";

type Props = {
  tournament: TournamentFragment;
  site: Site;
  isMobile: boolean;
  walletCurrency?: string;
  translations?: GamePageTranslations;
  isSingle?: boolean;
};

const TournamentPromoBanner: FC<Props> = ({
  tournament,
  site,
  isMobile,
  walletCurrency,
  translations,
  isSingle,
}) => {
  const {
    isOptedIn,
    isPending,
    handleOptIn,
    myScore: freshMyScore,
  } = useTournamentOptIn({
    tournamentId: tournament.id,
    currency: walletCurrency,
    isOptedIn: Boolean(tournament.iAmOptedIn),
  });
  const { bg, bgMobile, fg, fgMobile } = getGamePageBanners({
    mode: "tournament",
    site,
  });
  const isMobileViewport = useMediaQuery(MOBILE_MEDIA_QUERY, isMobile);
  const myScore = freshMyScore ?? tournament.myScore;

  return (
    <PromoBanner
      data-testid="tournament-promo-banner"
      fgCenter={isSingle}
      banner={isMobileViewport ? bgMobile : bg}
      bannerForeground={fg}
      bannerForegroundMobile={fgMobile}
      title={
        tournament.name ??
        translatePhrase(
          translations,
          "game.tournament.fallbackname",
          "Tournament",
        )
      }
      description={tournament.prizeText ?? undefined}
      timeRemains={getSecondsUntil(tournament.closeAt)}
      infoButton={
        <TournamentReadMoreButton
          shortUrl={tournament.shortUrl}
          translations={translations}
        />
      }
    >
      {!isOptedIn && (
        <Button
          variant="secondary"
          data-testid="tournament-opt-in"
          disabled={isOptedIn || isPending}
          onClick={handleOptIn}
          className="w-fit font-semibold max-sm:mt-3"
        >
          {tournamentButtonLabel({ isPending, translations })}
        </Button>
      )}
      {isOptedIn && (
        <MyTournamentStanding
          position={myScore?.position}
          name={myScore?.username}
          points={myScore?.points}
          translations={translations}
          site={site}
        />
      )}
    </PromoBanner>
  );
};

export default TournamentPromoBanner;
